import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, PackageOpen } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api, imgUrl, usd } from "@/lib/api";
import { useLang } from "@/i18n";

export default function Downloads() {
  const [products, setProducts] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const grouped = useMemo(() => {
    const withCatalogue = (products || []).filter((p) => p.catalogue);
    const groups = {};
    for (const p of withCatalogue) {
      (groups[p.category] = groups[p.category] || []).push(p);
    }
    return Object.entries(groups);
  }, [products]);

  return (
    <main data-testid="downloads-page" className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t("downloads_label")}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t("downloads_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-xl mb-14">{t("downloads_sub")}</p>
      </Reveal>

      {products === null && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-white/[0.03] border border-white/10 animate-pulse" />
          ))}
        </div>
      )}

      {products !== null && grouped.length === 0 && (
        <div data-testid="downloads-empty" className="border border-dashed border-white/15 p-16 text-center">
          <PackageOpen size={40} className="text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">{t("downloads_empty")}</p>
        </div>
      )}

      <div className="space-y-14">
        {grouped.map(([category, items]) => (
          <div key={category}>
            <Reveal>
              <h2 className="font-display font-semibold text-white text-2xl mb-6 flex items-center gap-4">
                {category}
                <span className="flex-1 h-px bg-white/10" />
              </h2>
            </Reveal>
            <div className="space-y-3">
              {items.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <div
                    data-testid={`download-row-${p.id}`}
                    className="group flex flex-wrap items-center gap-4 bg-[#0F172A] border border-white/10 p-5 hover:border-[#ED1C24]/50 transition-colors duration-300"
                  >
                    <img src={imgUrl(p.image)} alt="" className="w-14 h-14 object-cover" />
                    <div className="flex-1 min-w-[200px]">
                      <Link to={`/product/${p.id}`} className="font-display font-medium text-white hover:text-[#FFC61A] transition-colors">
                        {p.name}
                      </Link>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {p.brand ? `${p.brand} · ` : ""}{usd(p.price)}
                      </p>
                    </div>
                    <a
                      data-testid={`download-btn-${p.id}`}
                      href={imgUrl(p.catalogue)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 border border-white/15 text-slate-200 hover:border-[#ED1C24] hover:bg-[#ED1C24] hover:text-white text-xs font-medium px-5 py-2.5 transition-colors duration-300"
                    >
                      <FileDown size={15} /> {t("download_catalogue")}
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
